import { useState } from 'react'

import { Check, Copy } from '../icon/solar-icons.js'
import './copy-button.css'

export interface CopyButtonProps {
    /** Plain text put on the clipboard. */
    value: string
    /** Accessible name, e.g. "Kopeeri IBAN". */
    label: string
    /** Shown for two seconds after a successful copy, e.g. "Kopeeritud". */
    copiedLabel: string
}

/** Clipboard API first; a hidden textarea + execCommand when it is unavailable or refuses. */
const copyText = async (value: string): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(value)
        return true
    } catch {
        const area = document.createElement('textarea')
        area.value = value
        area.setAttribute('readonly', '')
        area.style.position = 'fixed'
        area.style.opacity = '0'
        document.body.appendChild(area)
        area.select()
        const ok = document.execCommand('copy')
        area.remove()
        return ok
    }
}

/**
 * Small inline icon button that copies `value` and briefly confirms it. The only state is
 * the two-second confirmation, so it stays a self-contained affordance next to a value.
 */
export const CopyButton = ({ value, label, copiedLabel }: CopyButtonProps) => {
    const [isCopied, setCopied] = useState(false)
    return (
        <button
            type="button"
            className={`copy-button${isCopied ? ' copy-button--copied' : ''}`}
            aria-label={isCopied ? copiedLabel : label}
            title={isCopied ? copiedLabel : label}
            onClick={() => {
                void copyText(value).then((ok) => {
                    if (!ok) return
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                })
            }}
        >
            {isCopied ? <Check size={16} /> : <Copy size={16} />}
            {isCopied && <span className="copy-button__text">{copiedLabel}</span>}
        </button>
    )
}
